/**
 * Minimal word-level diff (LCS) used to highlight what a quick edit /
 * voice revision changed in a note section. No deps.
 */
export interface DiffPart {
  type: 'same' | 'added' | 'removed'
  text: string
}

export function diffWords(oldText: string, newText: string): DiffPart[] {
  if (oldText === newText) return [{ type: 'same', text: newText }]
  // Tokenize into words that KEEP their trailing whitespace, so a changed phrase
  // renders as one contiguous highlight (e.g. "1 month") instead of separate boxes
  // around each word with plain gaps between them.
  const tokenize = (s: string): string[] => s.match(/\S+\s*|\s+/g) || []
  const a = tokenize(oldText)
  const b = tokenize(newText)
  const n = a.length
  const m = b.length
  // Safety valve for pathological sizes (sections are normally tiny)
  if (n * m > 500_000) {
    return [
      { type: 'removed', text: oldText },
      { type: 'same', text: '\n' },
      { type: 'added', text: newText },
    ]
  }
  // LCS lengths
  const dp: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const parts: DiffPart[] = []
  const push = (type: DiffPart['type'], text: string) => {
    const last = parts[parts.length - 1]
    if (last && last.type === type) last.text += text
    else parts.push({ type, text })
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('same', a[i])
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push('removed', a[i])
      i++
    } else {
      push('added', b[j])
      j++
    }
  }
  while (i < n) push('removed', a[i++])
  while (j < m) push('added', b[j++])
  return parts
}
